// UsersPanel Members / Invited / Deactivated tabs are server sources fetched by
// usePaginatedQuery. The endpoint builders below are the fetch URLs; the key
// factory owns the *cache* keys. Both are the single source of truth — the tab
// passes `endpoint` (URL) + `queryKey` (from the factory), and the mutations
// that invalidate the list reference the same factory key, so the two can never
// drift.
export const organizationEndpoints = {
  users: (organizationId: string) => `/organizations/${organizationId}/users`,
  invites: (organizationId: string) =>
    `/organizations/${organizationId}/invites`,
  deactivatedUsers: (organizationId: string) =>
    `/organizations/${organizationId}/deactivated-users`,
}

// Entity-rooted, hierarchical keys (generic → specific). Every list key ends in
// 'list' with the paginated params appended at call time by usePaginatedQuery,
// so a mutation invalidates the whole list by its params-less prefix.
export const organizationKeys = {
  // System-admin all-organisations list.
  all: () => ['organizations', 'all'] as const,
  // A single org resolved by slug (GET /organizations/by-slug/:slug) — the
  // super-user fallback when the org is absent from the /me membership list.
  bySlug: (slug: string) => ['organizations', 'by-slug', slug] as const,
  // Org-wide workspaces list (GET /organizations/:id/workspaces) — the
  // super-user move flow's source list.
  workspaces: (organizationId: string) =>
    ['organizations', organizationId, 'workspaces'] as const,
  summary: (organizationId: string) =>
    ['organizations', organizationId, 'summary'] as const,
  // A single org member's detail (GET /organizations/:id/users/:userId).
  userDetail: (organizationId: string, userId: string) =>
    ['organizations', organizationId, 'users', userId, 'detail'] as const,
  // Org-user typeahead search (GET /organizations/:id/users?query=), keyed by
  // the debounced query so each term caches independently.
  usersSearch: (organizationId: string, query: string) =>
    ['organizations', organizationId, 'users', 'search', query] as const,
  // Paginated list prefixes (params appended by the query) — invalidate to bust
  // every page / sort / search variant of the list at once.
  usersList: (organizationId: string) =>
    ['organizations', organizationId, 'users', 'list'] as const,
  invitesList: (organizationId: string) =>
    ['organizations', organizationId, 'invites', 'list'] as const,
  deactivatedList: (organizationId: string) =>
    ['organizations', organizationId, 'deactivated-users', 'list'] as const,
}
